import {
  Reason,
  StyledSubtitle,
} from '@/src/pages/AnytimeEligibilityV2/pages/Fallout/Fallout.styled';
import React from 'react';
import { Margin, P, TextLink } from '@ovotech/nebula';
import {
  VOLVO_APP_LINK,
  OVO_CHARGE_APP_LINK,
  VOLVO_CAR_APP_LINK,
  KIA_CAR_APP_LINK,
  HYUNDAI_CAR_APP_LINK,
  IntegrationType,
} from '@monovo/ev-anytime';
import { ReturnToHomePage } from '@/src/components/ReturnToHomePage/ReturnToHomePage';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';

const PARTNER_CONFIG: Partial<
  Record<
    IntegrationType,
    { name: string; initializedLink: string; activatedLink: string }
  >
> = {
  Kia: {
    name: 'Kia',
    initializedLink: KIA_CAR_APP_LINK,
    activatedLink: KIA_CAR_APP_LINK,
  },
  Hyundai: {
    name: 'Hyundai',
    initializedLink: HYUNDAI_CAR_APP_LINK,
    activatedLink: HYUNDAI_CAR_APP_LINK,
  },
  Volvo: {
    name: 'Volvo',
    initializedLink: VOLVO_APP_LINK,
    activatedLink: VOLVO_CAR_APP_LINK,
  },
};

const PartnerSubtitle = ({ name }: { name: string }) => (
  <StyledSubtitle>For {name} customers</StyledSubtitle>
);

const OtherSubtitle = () => (
  <StyledSubtitle>For all other customers</StyledSubtitle>
);

const BackToHome = () => (
  <Margin top={4}>
    <ReturnToHomePage />
  </Margin>
);

interface FalloutChargeAnytimeInitializedProps {
  signupIntegrationType?: IntegrationType;
}

export const FalloutChargeAnytimeInitialized: React.FC<
  FalloutChargeAnytimeInitializedProps
> = ({ signupIntegrationType }) => {
  const partner =
    signupIntegrationType && PARTNER_CONFIG[signupIntegrationType];

  return (
    <Reason data-testid="charge-anytime-initialized">
      <P>
        Good news. There’s no need to check your compatibility again. You can
        get Charge Anytime - you just need to complete your set-up.
      </P>
      {partner && (
        <>
          <PartnerSubtitle name={partner.name} />
          <P>
            To activate Charge Anytime, go to your{' '}
            <TextLink href={partner.initializedLink} opensInNewWindow>
              {partner.name} {partner.name === 'Volvo' ? 'Cars ' : ''}app
            </TextLink>{' '}
            and link your {partner.name} and OVO accounts. Once you've done
            this, you'll be able to manage your {partner.name} smart charging
            directly from your {partner.name}{' '}
            {partner.name === 'Volvo' ? 'Cars ' : ''}
            app.
          </P>
          <OtherSubtitle />
        </>
      )}
      <P>
        You can activate Charge Anytime by setting up smart charging through the{' '}
        <TextLink
          href={OVO_CHARGE_APP_LINK}
          opensInNewWindow
          data-testid="ovo-charge-app-link-initialised"
        >
          OVO Charge app
        </TextLink>
        . Simply access the app, link with your OVO account, and follow the
        steps to connect your home charger or EV.
      </P>
      <BackToHome />
    </Reason>
  );
};

interface FalloutChargeAnytimeActivatedProps {
  signupIntegrationType?: IntegrationType;
}

export const FalloutChargeAnytimeActivated: React.FC<
  FalloutChargeAnytimeActivatedProps
> = ({ signupIntegrationType }) => {
  const partner =
    signupIntegrationType && PARTNER_CONFIG[signupIntegrationType];

  return (
    <Reason data-testid="charge-anytime-activated">
      <P>
        Good news. There’s no need to check your compatibility again. You’re all
        set up.
      </P>
      {partner && (
        <>
          <PartnerSubtitle name={partner.name} />
          <p>
            To manage your {partner.name} smart charging, simply go to your{' '}
            <TextLink href={partner.activatedLink} opensInNewWindow>
              {partner.name} {partner.name === 'Volvo' ? 'Cars ' : ''}app
            </TextLink>{' '}
            and start charging.
          </p>
          <OtherSubtitle />
        </>
      )}
      <P>
        You can smart charge through the{' '}
        <TextLink
          href={OVO_CHARGE_APP_LINK}
          opensInNewWindow
          data-testid="ovo-charge-app-link"
        >
          OVO Charge app
        </TextLink>
        .
      </P>
      <BackToHome />
    </Reason>
  );
};

export const FalloutChargeAnytime = () => {
  const {
    payAsYouDriveBoltonActive,
    payAsYouDriveBoltonInitialised,
    hasActivePayMonthlyBolton,
    hasInitializedPayMonthlyBolton,
    signupIntegrationType,
  } = useChargeAnytimeBoltonState();
  if (payAsYouDriveBoltonActive || hasActivePayMonthlyBolton) {
    return (
      <FalloutChargeAnytimeActivated
        signupIntegrationType={signupIntegrationType}
      />
    );
  }
  if (payAsYouDriveBoltonInitialised || hasInitializedPayMonthlyBolton) {
    return (
      <FalloutChargeAnytimeInitialized
        signupIntegrationType={signupIntegrationType}
      />
    );
  }
  return null;
};
